/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

/**
 * Hook to fetch organization-level budget rates
 * Fetches from organization's default budget or settings
 */

import { useAuthSafe } from "@/hooks/auth/use-auth-safe";
import { useOrganizationQueryGuard } from "@/hooks/use-organization-query";
import { createApiClient, isApiFailure } from "@/lib/api/api-client";
import type { BudgetRates } from "@/lib/business-logic/estimates";
import {
  QUERY_GC_TIME,
  QUERY_RETRY,
  QUERY_STALE_TIME,
  getRetryDelay,
} from "@/lib/constants/query-config";
import { logger } from "@/lib/utils/logger";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

interface UseOrganizationBudgetRatesOptions {
  enabled?: boolean;
  divisionId?: string | null;
  budgetId?: string | null;
}

interface UseOrganizationBudgetRatesReturn {
  budgetRates: BudgetRates | null;
  budgetId: string | null;
  budgetName: string | null;
  source: "budget" | "org_settings" | null;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Hook to fetch organization-level budget rates
 * Looks for default budget or organization settings
 */
export function useOrganizationBudgetRates({
  enabled = true,
  divisionId,
  budgetId,
}: UseOrganizationBudgetRatesOptions = {}): UseOrganizationBudgetRatesReturn {
  const { getToken } = useAuthSafe();
  const { organizationId, queryEnabled } = useOrganizationQueryGuard({
    enabled,
    debugKey: "useOrganizationBudgetRates",
  });

  const api = useMemo(
    () =>
      createApiClient({
        getToken,
        getOrganizationId: () => organizationId ?? undefined,
        autoRetry429: true,
      }),
    [getToken, organizationId],
  );

  const queryKey = useMemo(
    () => [
      "organization-budget-rates",
      organizationId,
      divisionId ?? null,
      budgetId ?? null,
    ],
    [organizationId, divisionId, budgetId],
  );

  interface BudgetRatesResponse {
    budgetRates: BudgetRates | null;
    budgetId: string | null;
    budgetName: string | null;
    source: "budget" | "org_settings" | null;
  }

  const { data, isLoading, error } = useQuery<BudgetRatesResponse>({
    queryKey,
    queryFn: async (): Promise<BudgetRatesResponse> => {
      if (!organizationId) {
        return {
          budgetRates: null,
          budgetId: null,
          budgetName: null,
          source: null,
        };
      }

      try {
        const params = new URLSearchParams();
        if (divisionId) params.set("divisionId", divisionId);
        if (budgetId) params.set("budgetId", budgetId);
        const qs = params.toString();
        const res = await api.get<{
          budgetRates?: BudgetRates | null;
          budgetId?: string | null;
          budgetName?: string | null;
          source?: "budget" | "org_settings";
        }>(
          `/api/organizations/${organizationId}/budget-rates${qs ? `?${qs}` : ""}`,
        );

        if (isApiFailure(res)) {
          if (res.code !== "RESOURCE_NOT_FOUND") {
          }
          logger.info("No budget rates found for organization", {
            organizationId,
            code: res.code,
          });
          return {
            budgetRates: null,
            budgetId: null,
            budgetName: null,
            source: null,
          };
        }

        return {
          budgetRates: res.data?.budgetRates ?? null,
          budgetId: res.data?.budgetId ?? null,
          budgetName: res.data?.budgetName ?? null,
          source: res.data?.source ?? null,
        };
      } catch (err) {
        logger.error("Failed to fetch organization budget rates", {
          error: err,
          organizationId,
        });
        return {
          budgetRates: null,
          budgetId: null,
          budgetName: null,
          source: null,
        };
      }
    },
    enabled: queryEnabled,
    staleTime: QUERY_STALE_TIME.STANDARD,
    gcTime: QUERY_GC_TIME.STANDARD,
    retry: QUERY_RETRY.ONCE,
    retryDelay: getRetryDelay,
    refetchOnWindowFocus: false,
  });

  return {
    budgetRates: data?.budgetRates ?? null,
    budgetId: data?.budgetId ?? null,
    budgetName: data?.budgetName ?? null,
    source: data?.source ?? null,
    isLoading,
    error: error as Error | null,
  };
}
